import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import {
  Shield,
  Key,
  Folder,
  Home,
  ChevronRight,
  RefreshCw,
  Plus,
  FileText,
  Edit,
  Trash2,
  Eye,
  EyeOff,
  Copy,
  Download,
  Upload,
  MoreHorizontal,
  Settings,
  Check,
  X,
} from "lucide-react";
import { ModeToggle } from "@/components/ui/mode-toggle";

interface VaultHealth {
  success: boolean;
  status?: any;
  error?: string;
}

interface Secret {
  name: string;
  data: { [key: string]: any };
  metadata?: any;
}

interface KeyValuePair {
  key: string;
  value: string;
}

export function VaultManager() {
  const { user, logout } = useAuth();
  const [vaultHealth, setVaultHealth] = useState<VaultHealth | null>(null);
  const [secrets, setSecrets] = useState<string[]>([]);
  const [filteredSecrets, setFilteredSecrets] = useState<string[]>([]);
  const [currentPath, setCurrentPath] = useState("");
  const [mountPath, setMountPath] = useState("secret");
  const [selectedSecret, setSelectedSecret] = useState<Secret | null>(null);
  const [loading, setLoading] = useState(false);
  const [showValues, setShowValues] = useState<{ [key: string]: boolean }>({});
  const [searchTerm, setSearchTerm] = useState("");
  const [viewMode, setViewMode] = useState<"explorer" | "secret">("explorer");
  const [editingKeys, setEditingKeys] = useState<{ [key: string]: boolean }>(
    {},
  );
  const [editValues, setEditValues] = useState<{ [key: string]: string }>({});
  const [isEditingRawJson, setIsEditingRawJson] = useState(false);
  const [rawJsonValue, setRawJsonValue] = useState("");
  const [isAddingNewKey, setIsAddingNewKey] = useState(false);
  const [newKeyName, setNewKeyName] = useState("");
  const [newKeyValue, setNewKeyValue] = useState("");
  const [editingKeyNames, setEditingKeyNames] = useState<{
    [key: string]: boolean;
  }>({});
  const [editKeyNames, setEditKeyNames] = useState<{ [key: string]: string }>(
    {},
  );

  // Create/Edit Dialog States
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isSettingsDialogOpen, setIsSettingsDialogOpen] = useState(false);
  const [folderToDelete, setFolderToDelete] = useState<{
    name: string;
    secretCount: number;
    folderCount: number;
    totalItems: number;
  } | null>(null);
  const [keyToDelete, setKeyToDelete] = useState<string | null>(null);
  const [newItemName, setNewItemName] = useState("");
  const [newItemType, setNewItemType] = useState<"secret" | "folder">("secret");

  // Filter secrets based on search term
  useEffect(() => {
    if (!searchTerm) {
      setFilteredSecrets(secrets);
    } else {
      const filtered = secrets.filter((secret) =>
        secret.toLowerCase().includes(searchTerm.toLowerCase()),
      );
      setFilteredSecrets(filtered);
    }
  }, [secrets, searchTerm]);

  // Initial data loading
  useEffect(() => {
    checkVaultHealth();
    listSecrets();
  }, []);

  // Reload when path or mount changes
  useEffect(() => {
    listSecrets();
    // Clear the search filter when navigating so the user sees the
    // full contents of the folder they just entered (or when changing mount).
    setSearchTerm("");
  }, [mountPath, currentPath]);

  const checkVaultHealth = async () => {
    try {
      const response = await fetch("/api/vault/health");
      const data = await response.json();
      setVaultHealth(data);
    } catch (error) {
      console.error("Health check failed:", error);
      setVaultHealth({ success: false, error: "Connection failed" });
    }
  };

  const listSecrets = async (showSuccessToast = false) => {
    setLoading(true);
    try {
      const url = currentPath
        ? `/api/vault/list/${mountPath}/${currentPath}`
        : `/api/vault/list/${mountPath}`;

      console.log(`Fetching: ${url}`);
      const response = await fetch(url);
      const data = await response.json();
      console.log("List response:", data);

      if (data.success) {
        const keys = data.keys || [];
        setSecrets(keys);

        if (showSuccessToast && keys.length > 0) {
          const folders = keys.filter((key: string) =>
            key.endsWith("/"),
          ).length;
          const secrets = keys.length - folders;
          toast.success(
            `Found ${keys.length} items (${folders} folders, ${secrets} secrets)`,
          );
        } else if (showSuccessToast && keys.length === 0) {
          toast.info("No secrets found at this path");
        }
      } else {
        console.error("List failed:", data.error);
        setSecrets([]);
        toast.error(data.error || "Failed to list secrets");
      }
    } catch (error) {
      console.error("List error:", error);
      setSecrets([]);
      toast.error("Failed to list secrets");
    } finally {
      setLoading(false);
    }
  };

  const readSecret = async (path: string) => {
    try {
      const fullPath = currentPath ? `${currentPath}/${path}` : path;
      const url = `/api/vault/secrets/${mountPath}/${fullPath}`;

      console.log(`Reading secret: ${url}`);
      const response = await fetch(url);
      const data = await response.json();

      if (data.success) {
        setSelectedSecret({
          name: path,
          data: data.data,
          metadata: data.metadata,
        });
        setViewMode("secret");
        console.log("Selected secret set:", { name: path, data: data.data });
        toast.success("Secret loaded successfully");
      } else {
        console.error("Read failed:", data.error);
        toast.error(data.error || "Failed to read secret");
      }
    } catch (error) {
      console.error("Read error:", error);
      toast.error("Failed to read secret");
    }
  };

  const createItem = async () => {
    if (!newItemName.trim()) {
      toast.error("Please enter a name");
      return;
    }

    try {
      if (newItemType === "folder") {
        // Create folder with placeholder secret
        const folderPath = currentPath
          ? `${currentPath}/${newItemName}`
          : newItemName;
        const placeholderSecretPath = `${folderPath}/.placeholder`;

        const response = await fetch("/api/vault/secrets", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            mountPath,
            path: placeholderSecretPath,
            data: {
              _type: "folder-placeholder",
              _created: new Date().toISOString(),
              _description: `Placeholder secret to maintain folder structure for '${newItemName}'`,
              _note:
                "This secret can be safely deleted once other secrets are added to this folder",
            },
          }),
        });

        const data = await response.json();

        if (data.success) {
          toast.success(`Folder "${newItemName}" created successfully`);
          setIsCreateDialogOpen(false);
          setNewItemName("");
          setTimeout(() => {
            listSecrets();
          }, 500);
        } else {
          toast.error(data.error || "Failed to create folder");
        }
      } else {
        // Create empty secret with placeholder key
        const secretPath = currentPath
          ? `${currentPath}/${newItemName}`
          : newItemName;

        const response = await fetch("/api/vault/secrets", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            mountPath,
            path: secretPath,
            data: {
              _edit_me: "Add your secret data here",
            },
          }),
        });

        const data = await response.json();

        if (data.success) {
          toast.success(`Secret "${newItemName}" created successfully`);
          setIsCreateDialogOpen(false);
          setNewItemName("");
          // Immediately read and open the new secret for editing
          readSecret(newItemName);
        } else {
          toast.error(data.error || "Failed to create secret");
        }
      }
    } catch (error) {
      console.error("Create error:", error);
      toast.error("Failed to create item");
    }
  };

  const deleteSecret = async (path: string) => {
    try {
      const fullPath = currentPath ? `${currentPath}/${path}` : path;
      const url = `/api/vault/secrets/${mountPath}/${fullPath}`;

      const response = await fetch(url, { method: "DELETE" });
      const data = await response.json();

      if (data.success) {
        toast.success("Secret deleted successfully");
        listSecrets();
        if (selectedSecret?.name === path) {
          setSelectedSecret(null);
          setViewMode("explorer");
        }
      } else {
        toast.error(data.error || "Failed to delete secret");
      }
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("Failed to delete secret");
    }
  };

  const confirmDeleteFolder = async (folderPath: string) => {
    try {
      const folderName = folderPath.endsWith("/")
        ? folderPath.slice(0, -1)
        : folderPath;
      const fullPath = currentPath
        ? `${currentPath}/${folderName}`
        : folderName;

      // First, list all secrets in the folder to see what will be deleted
      const listUrl = currentPath
        ? `/api/vault/list/${mountPath}/${fullPath}`
        : `/api/vault/list/${mountPath}/${fullPath}`;

      const listResponse = await fetch(listUrl);
      const listData = await listResponse.json();

      if (!listData.success) {
        toast.error("Failed to list folder contents");
        return;
      }

      const items = listData.keys || [];
      const secretsToDelete = items.filter(
        (item: string) => !item.endsWith("/"),
      );
      const subFolders = items.filter((item: string) => item.endsWith("/"));

      if (items.length === 0) {
        toast.info("Folder is empty");
        return;
      }

      // Show confirmation modal with details
      const secretCount = secretsToDelete.length;
      const folderCount = subFolders.length;
      const totalItems = secretCount + folderCount;

      setFolderToDelete({
        name: folderName,
        secretCount,
        folderCount,
        totalItems,
      });
    } catch (error) {
      console.error("Delete folder confirmation error:", error);
      toast.error("Failed to analyze folder contents");
    }
  };

  const deleteFolder = async () => {
    if (!folderToDelete) return;

    try {
      console.log("Starting folder deletion for:", folderToDelete.name);

      const fullPath = currentPath
        ? `${currentPath}/${folderToDelete.name}`
        : folderToDelete.name;

      console.log("Full path to delete:", fullPath);

      // Delete all secrets in the folder recursively
      let deletedCount = 0;
      let failedCount = 0;

      const deleteRecursively = async (basePath: string) => {
        console.log("Deleting recursively in path:", basePath);

        const url = `/api/vault/list/${mountPath}/${basePath}`;
        const response = await fetch(url);
        const data = await response.json();

        if (!data.success) {
          console.log("Failed to list contents of:", basePath);
          return;
        }

        const items = data.keys || [];
        console.log("Found items in", basePath, ":", items);

        for (const item of items) {
          const itemPath = basePath ? `${basePath}/${item}` : item;

          if (item.endsWith("/")) {
            // Recursively delete subfolder
            console.log("Recursing into subfolder:", itemPath);
            await deleteRecursively(itemPath.slice(0, -1));
          } else {
            // Delete secret
            console.log("Deleting secret:", itemPath);
            try {
              const deleteUrl = `/api/vault/secrets/${mountPath}/${itemPath}`;
              const deleteResponse = await fetch(deleteUrl, {
                method: "DELETE",
              });
              const deleteData = await deleteResponse.json();

              if (deleteData.success) {
                deletedCount++;
                console.log("Successfully deleted:", itemPath);
              } else {
                failedCount++;
                console.log("Failed to delete:", itemPath, deleteData.error);
              }
            } catch (error) {
              failedCount++;
              console.error("Error deleting secret:", itemPath, error);
            }
          }
        }
      };

      await deleteRecursively(fullPath);

      console.log(
        "Deletion complete. Deleted:",
        deletedCount,
        "Failed:",
        failedCount,
      );

      if (deletedCount > 0) {
        toast.success(
          `Deleted ${deletedCount} item(s) from folder "${folderToDelete.name}"`,
        );
      }
      if (failedCount > 0) {
        toast.error(`Failed to delete ${failedCount} item(s)`);
      }

      // Refresh the current view
      console.log("Refreshing list...");
      try {
        // Add a small delay to ensure Vault has processed all deletions
        await new Promise((resolve) => setTimeout(resolve, 500));
        await listSecrets();
        console.log("List refresh completed");
      } catch (refreshError) {
        console.error("Refresh error after folder deletion:", refreshError);
        // Don't show error toast for refresh failure after successful deletion
        // The folder was deleted successfully, just the UI refresh failed
      }
    } catch (error) {
      console.error("Delete folder error:", error);
      toast.error(
        `Failed to delete folder: ${error instanceof Error ? error.message : "Unknown error"}`,
      );
    }
  };

  const toggleValueVisibility = (key: string) => {
    setShowValues((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const navigateToPath = (path: string) => {
    if (path.endsWith("/")) {
      // This is a folder - navigate into it by setting the current path
      const newPath = currentPath
        ? `${currentPath}/${path.slice(0, -1)}`
        : path.slice(0, -1);
      setCurrentPath(newPath);
    } else {
      // This is a secret - read its contents
      readSecret(path);
    }
  };

  const navigateUp = () => {
    if (currentPath) {
      const parts = currentPath.split("/");
      parts.pop();
      setCurrentPath(parts.join("/"));
    }
  };

  const resetCreateForm = () => {
    setNewItemName("");
    setNewItemType("secret");
  };

  const getCurrentBreadcrumb = () => {
    return (
      <div className="flex items-center gap-1 text-sm">
        <Button
          variant="ghost"
          size="sm"
          className="h-auto p-1 hover:bg-muted/50"
          onClick={() => setCurrentPath("")}
        >
          <Home className="h-4 w-4" />
        </Button>

        <ChevronRight className="h-4 w-4 text-muted-foreground" />

        <Button
          variant="ghost"
          size="sm"
          className="h-auto p-1 font-medium hover:bg-muted/50"
          onClick={() => setCurrentPath("")}
        >
          {mountPath}
        </Button>

        {currentPath &&
          currentPath.split("/").map((part, index, arr) => (
            <React.Fragment key={index}>
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
              <Button
                variant="ghost"
                size="sm"
                className="h-auto p-1 hover:bg-muted/50"
                onClick={() => {
                  const newPath = arr.slice(0, index + 1).join("/");
                  setCurrentPath(newPath);
                }}
              >
                {part}
              </Button>
            </React.Fragment>
          ))}
      </div>
    );
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied to clipboard");
    } catch (error) {
      toast.error("Failed to copy");
    }
  };

  const startEditing = (key: string, value: any) => {
    setEditingKeys((prev) => ({ ...prev, [key]: true }));
    // Format value for editing - stringify objects, keep strings as-is
    const editValue =
      typeof value === "object" && value !== null
        ? JSON.stringify(value, null, 2)
        : String(value);
    setEditValues((prev) => ({ ...prev, [key]: editValue }));
  };

  const cancelEditing = (key: string) => {
    setEditingKeys((prev) => ({ ...prev, [key]: false }));
    setEditValues((prev) => {
      const newValues = { ...prev };
      delete newValues[key];
      return newValues;
    });
  };

  const saveEdit = async (key: string) => {
    if (!selectedSecret) return;

    try {
      const newValue = editValues[key];
      if (newValue === undefined) return;

      // Try to parse as JSON if it looks like JSON, otherwise keep as string
      let processedValue = newValue;
      try {
        if (
          newValue.trim().startsWith("{") ||
          newValue.trim().startsWith("[")
        ) {
          processedValue = JSON.parse(newValue);
        }
      } catch {
        // Keep as string if JSON parsing fails
        processedValue = newValue;
      }

      // Update the secret with the new value
      const updatedData = {
        ...selectedSecret.data,
        [key]: processedValue,
      };

      const response = await fetch("/api/vault/secrets", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          mountPath,
          path: currentPath
            ? `${currentPath}/${selectedSecret.name}`
            : selectedSecret.name,
          data: updatedData,
        }),
      });

      const data = await response.json();

      if (data.success) {
        // Update local state
        setSelectedSecret((prev) =>
          prev ? { ...prev, data: updatedData } : prev,
        );
        toast.success(`Updated "${key}" successfully`);

        // Exit editing mode
        setEditingKeys((prev) => ({ ...prev, [key]: false }));
        setEditValues((prev) => {
          const newValues = { ...prev };
          delete newValues[key];
          return newValues;
        });
      } else {
        toast.error(data.error || "Failed to update secret");
      }
    } catch (error) {
      console.error("Save edit error:", error);
      toast.error("Failed to save changes");
    }
  };

  const formatValueForDisplay = (value: any) => {
    if (typeof value === "object" && value !== null) {
      return JSON.stringify(value, null, 2);
    }
    return String(value);
  };

  const startEditingRawJson = () => {
    if (!selectedSecret) return;
    setIsEditingRawJson(true);
    setRawJsonValue(JSON.stringify(selectedSecret.data, null, 2));
  };

  const cancelEditingRawJson = () => {
    setIsEditingRawJson(false);
    setRawJsonValue("");
  };

  const saveRawJsonEdit = async () => {
    if (!selectedSecret) return;

    try {
      const parsedData = JSON.parse(rawJsonValue);

      const response = await fetch("/api/vault/secrets", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          mountPath,
          path: currentPath
            ? `${currentPath}/${selectedSecret.name}`
            : selectedSecret.name,
          data: parsedData,
        }),
      });

      const data = await response.json();

      if (data.success) {
        // Update local state
        setSelectedSecret((prev) =>
          prev ? { ...prev, data: parsedData } : prev,
        );
        toast.success("Secret updated successfully");

        // Exit editing mode
        setIsEditingRawJson(false);
        setRawJsonValue("");
      } else {
        toast.error(data.error || "Failed to update secret");
      }
    } catch (error) {
      if (error instanceof SyntaxError) {
        toast.error("Invalid JSON format");
      } else {
        console.error("Save raw JSON error:", error);
        toast.error("Failed to save changes");
      }
    }
  };

  const startAddingNewKey = () => {
    setIsAddingNewKey(true);
    setNewKeyName("");
    setNewKeyValue("");
  };

  const cancelAddingNewKey = () => {
    setIsAddingNewKey(false);
    setNewKeyName("");
    setNewKeyValue("");
  };

  const saveNewKey = async () => {
    if (!selectedSecret || !newKeyName.trim()) {
      toast.error("Please enter a key name");
      return;
    }

    if (selectedSecret.data[newKeyName]) {
      toast.error("Key already exists");
      return;
    }

    try {
      // Process the value (try to parse as JSON if it looks like JSON)
      let processedValue = newKeyValue;
      try {
        if (
          newKeyValue.trim().startsWith("{") ||
          newKeyValue.trim().startsWith("[")
        ) {
          processedValue = JSON.parse(newKeyValue);
        }
      } catch {
        // Keep as string if JSON parsing fails
        processedValue = newKeyValue;
      }

      // Update the secret with the new key-value pair
      const updatedData = {
        ...selectedSecret.data,
        [newKeyName]: processedValue,
      };

      const response = await fetch("/api/vault/secrets", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          mountPath,
          path: currentPath
            ? `${currentPath}/${selectedSecret.name}`
            : selectedSecret.name,
          data: updatedData,
        }),
      });

      const data = await response.json();

      if (data.success) {
        // Update local state
        setSelectedSecret((prev) =>
          prev ? { ...prev, data: updatedData } : prev,
        );
        toast.success(`Added "${newKeyName}" successfully`);

        // Exit adding mode
        setIsAddingNewKey(false);
        setNewKeyName("");
        setNewKeyValue("");
      } else {
        toast.error(data.error || "Failed to add key");
      }
    } catch (error) {
      console.error("Add new key error:", error);
      toast.error("Failed to add new key");
    }
  };

  const startEditingKeyName = (oldKey: string) => {
    setEditingKeyNames((prev) => ({ ...prev, [oldKey]: true }));
    setEditKeyNames((prev) => ({ ...prev, [oldKey]: oldKey }));
  };

  const cancelEditingKeyName = (oldKey: string) => {
    setEditingKeyNames((prev) => ({ ...prev, [oldKey]: false }));
    setEditKeyNames((prev) => {
      const newNames = { ...prev };
      delete newNames[oldKey];
      return newNames;
    });
  };

  const saveKeyNameEdit = async (oldKey: string) => {
    if (!selectedSecret) return;

    const newKey = editKeyNames[oldKey];
    if (!newKey || newKey === oldKey) {
      cancelEditingKeyName(oldKey);
      return;
    }

    if (selectedSecret.data[newKey]) {
      toast.error("Key name already exists");
      return;
    }

    try {
      const updatedData = { ...selectedSecret.data };
      updatedData[newKey] = updatedData[oldKey];
      delete updatedData[oldKey];

      const response = await fetch("/api/vault/secrets", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          mountPath,
          path: currentPath
            ? `${currentPath}/${selectedSecret.name}`
            : selectedSecret.name,
          data: updatedData,
        }),
      });

      const data = await response.json();

      if (data.success) {
        setSelectedSecret((prev) =>
          prev ? { ...prev, data: updatedData } : prev,
        );
        toast.success(`Renamed "${oldKey}" to "${newKey}"`);
        setEditingKeyNames((prev) => ({ ...prev, [oldKey]: false }));
        setEditKeyNames((prev) => {
          const newNames = { ...prev };
          delete newNames[oldKey];
          return newNames;
        });
      } else {
        toast.error(data.error || "Failed to rename key");
      }
    } catch (error) {
      console.error("Rename key error:", error);
      toast.error("Failed to rename key");
    }
  };

  const deleteKey = async (keyName: string) => {
    if (!selectedSecret) return;

    setKeyToDelete(keyName);
  };

  const confirmDeleteKey = async () => {
    if (!selectedSecret || !keyToDelete) return;

    try {
      const updatedData = { ...selectedSecret.data };
      delete updatedData[keyToDelete];

      const response = await fetch("/api/vault/secrets", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          mountPath,
          path: currentPath
            ? `${currentPath}/${selectedSecret.name}`
            : selectedSecret.name,
          data: updatedData,
        }),
      });

      const data = await response.json();

      if (data.success) {
        setSelectedSecret((prev) =>
          prev ? { ...prev, data: updatedData } : prev,
        );
        toast.success(`Deleted "${keyToDelete}"`);
        setKeyToDelete(null);
      } else {
        toast.error(data.error || "Failed to delete key");
      }
    } catch (error) {
      console.error("Delete key error:", error);
      toast.error("Failed to delete key");
    } finally {
      setKeyToDelete(null);
    }
  };

  return (
    <div className="h-screen w-full bg-background flex flex-col">
      {/* Header */}
      <div className="w-full bg-background border-b border-border flex-shrink-0">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Shield className="h-8 w-8 text-blue-500" />
              <h1 className="text-3xl font-bold">Vault Secret Manager</h1>
            </div>
            {/* Vault Health Status and Theme Toggle */}
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <div
                  className={`h-3 w-3 rounded-full ${
                    vaultHealth?.success
                      ? "bg-green-600 dark:bg-green-400 animate-pulse"
                      : "bg-gray-400 dark:bg-gray-600"
                  }`}
                />
                <span
                  className={`text-sm ${
                    vaultHealth?.success
                      ? "text-green-600 dark:text-green-400"
                      : "text-gray-500 dark:text-gray-400"
                  }`}
                >
                  {vaultHealth?.success ? "Connected" : "Disconnected"}
                </span>
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-xs">
                        {user?.name?.charAt(0).toUpperCase() || "U"}
                      </div>
                      {user?.name || "User"}
                    </div>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={logout}>Logout</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <ModeToggle />
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 p-6 flex flex-col min-h-0 overflow-hidden">
        {viewMode === "explorer" ? (
          /* File Explorer View */
          <Card className="flex-1 flex flex-col min-h-0">
            <CardHeader className="pb-4 border-b border-border">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CardTitle className="text-xl">Secret Explorer</CardTitle>
                  <Badge variant="outline" className="ml-2">
                    {filteredSecrets.length} items
                  </Badge>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => listSecrets(true)}
                    disabled={loading}
                  >
                    <RefreshCw
                      className={`h-4 w-4 ${loading ? "animate-spin" : ""}`}
                    />
                    Refresh
                  </Button>
                  <Dialog
                    open={isCreateDialogOpen}
                    onOpenChange={setIsCreateDialogOpen}
                  >
                    <DialogTrigger asChild>
                      <Button size="sm">
                        <Plus className="h-4 w-4" />
                        New
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-md">
                      <DialogHeader>
                        <DialogTitle>Create New Item</DialogTitle>
                        <DialogDescription>
                          Create a new secret or folder at: {mountPath}/
                          {currentPath}
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4">
                        <div>
                          <Label htmlFor="item-type">Type</Label>
                          <div className="flex gap-2 mt-1">
                            <Button
                              variant={
                                newItemType === "secret" ? "default" : "outline"
                              }
                              size="sm"
                              onClick={() => setNewItemType("secret")}
                              className="flex-1"
                            >
                              <Key className="h-4 w-4 mr-2" />
                              Secret
                            </Button>
                            <Button
                              variant={
                                newItemType === "folder" ? "default" : "outline"
                              }
                              size="sm"
                              onClick={() => setNewItemType("folder")}
                              className="flex-1"
                            >
                              <Folder className="h-4 w-4 mr-2" />
                              Folder
                            </Button>
                          </div>
                        </div>
                        <div>
                          <Label htmlFor="item-name">Name</Label>
                          <Input
                            id="item-name"
                            value={newItemName}
                            onChange={(e) => setNewItemName(e.target.value)}
                            placeholder={
                              newItemType === "secret"
                                ? "my-secret"
                                : "my-folder"
                            }
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                createItem();
                              } else if (e.key === "Escape") {
                                setIsCreateDialogOpen(false);
                                resetCreateForm();
                              }
                            }}
                          />
                        </div>
                      </div>
                      <div className="flex gap-2 justify-end">
                        <Button
                          variant="outline"
                          onClick={() => {
                            setIsCreateDialogOpen(false);
                            resetCreateForm();
                          }}
                        >
                          Cancel
                        </Button>
                        <Button onClick={createItem}>
                          Create{" "}
                          {newItemType === "secret" ? "Secret" : "Folder"}
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
              </div>

              {/* Breadcrumb Navigation */}
              <div className="mt-4">{getCurrentBreadcrumb()}</div>

              {/* Controls */}
              <div className="flex gap-4 mt-4">
                <div className="flex-1">
                  <Label htmlFor="search" className="text-sm font-medium">
                    Filter
                  </Label>
                  <Input
                    id="search"
                    type="text"
                    placeholder="Search secrets..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="mt-1"
                  />
                </div>
                <div className="flex items-end gap-2">
                  {currentPath && (
                    <Button onClick={navigateUp} variant="outline" size="sm">
                      <ChevronRight className="h-4 w-4 rotate-180" />
                      Back
                    </Button>
                  )}
                  <Dialog
                    open={isSettingsDialogOpen}
                    onOpenChange={setIsSettingsDialogOpen}
                  >
                    <DialogTrigger asChild>
                      <Button variant="outline" size="sm">
                        <Settings className="h-4 w-4" />
                        Settings
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-md">
                      <DialogHeader>
                        <DialogTitle>Advanced Settings</DialogTitle>
                        <DialogDescription>
                          Configure advanced Vault settings
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4">
                        <div>
                          <Label
                            htmlFor="mount-path-setting"
                            className="text-sm font-medium"
                          >
                            Mount Path
                          </Label>
                          <Input
                            id="mount-path-setting"
                            value={mountPath}
                            onChange={(e) => setMountPath(e.target.value)}
                            placeholder="secret"
                            className="mt-1"
                          />
                          <p className="text-xs text-muted-foreground mt-1">
                            The Vault mount path for secrets engine (usually
                            "secret")
                          </p>
                        </div>
                      </div>
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="outline"
                          onClick={() => setIsSettingsDialogOpen(false)}
                        >
                          Cancel
                        </Button>
                        <Button onClick={() => setIsSettingsDialogOpen(false)}>
                          Save Settings
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
              </div>
            </CardHeader>

            <CardContent className="flex-1 min-h-0 overflow-hidden">
              <div className="h-full p-4 overflow-y-auto">
                {loading ? (
                  <div className="flex items-center justify-center py-8">
                    <RefreshCw className="h-6 w-6 animate-spin mr-2" />
                    <span>Loading secrets...</span>
                  </div>
                ) : filteredSecrets.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-8 text-center">
                    <Folder className="h-12 w-12 text-muted-foreground mb-4" />
                    <h3 className="text-lg font-semibold mb-2">
                      No Items Found
                    </h3>
                    <p className="text-muted-foreground mb-4">
                      {searchTerm
                        ? "No secrets match your search criteria"
                        : "This path appears to be empty"}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {filteredSecrets.map((secret) => (
                      <Card
                        key={secret}
                        className="hover:bg-muted/50 transition-colors"
                      >
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              {secret.endsWith("/") ? (
                                <Folder className="h-5 w-5 text-blue-500" />
                              ) : (
                                <FileText className="h-5 w-5 text-green-500" />
                              )}
                              <span
                                className="font-medium cursor-pointer hover:text-primary"
                                onClick={() => navigateToPath(secret)}
                              >
                                {secret}
                              </span>
                              {secret.endsWith("/") && (
                                <Badge variant="secondary" className="text-xs">
                                  Folder
                                </Badge>
                              )}
                            </div>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm">
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                {!secret.endsWith("/") ? (
                                  <>
                                    <DropdownMenuItem
                                      onClick={() => readSecret(secret)}
                                    >
                                      <Eye className="h-4 w-4 mr-2" />
                                      View
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                      onClick={() => readSecret(secret)}
                                    >
                                      <Edit className="h-4 w-4 mr-2" />
                                      Edit
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem
                                      onClick={() => deleteSecret(secret)}
                                      className="text-red-600"
                                    >
                                      <Trash2 className="h-4 w-4 mr-2" />
                                      Delete Secret
                                    </DropdownMenuItem>
                                  </>
                                ) : (
                                  <>
                                    <DropdownMenuItem
                                      onClick={() => navigateToPath(secret)}
                                    >
                                      <Folder className="h-4 w-4 mr-2" />
                                      Open Folder
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem
                                      onClick={() =>
                                        confirmDeleteFolder(secret)
                                      }
                                      className="text-red-600"
                                    >
                                      <Trash2 className="h-4 w-4 mr-2" />
                                      Delete Folder
                                    </DropdownMenuItem>
                                  </>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        ) : (
          /* Secret Details View */
          <Card className="flex-1 flex flex-col min-h-0">
            <CardHeader className="pb-4 border-b border-border">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setViewMode("explorer");
                      setSelectedSecret(null);
                    }}
                    className="p-2"
                  >
                    <ChevronRight className="h-4 w-4 rotate-180" />
                  </Button>
                  <div className="p-2 rounded-full bg-green-100 dark:bg-green-900/50">
                    <Key className="h-5 w-5 text-green-600 dark:text-green-400" />
                  </div>
                  <div>
                    <CardTitle className="text-xl">
                      {selectedSecret?.name}
                    </CardTitle>
                    <CardDescription>
                      {selectedSecret
                        ? Object.keys(selectedSecret.data).length
                        : 0}{" "}
                      keys
                    </CardDescription>
                  </div>
                </div>
                <div className="flex gap-2">
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={!selectedSecret}
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete Secret</AlertDialogTitle>
                        <AlertDialogDescription>
                          Are you sure you want to delete "
                          {selectedSecret?.name}"? This action cannot be undone.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() =>
                            selectedSecret && deleteSecret(selectedSecret.name)
                          }
                          className="bg-red-600 hover:bg-red-700"
                        >
                          Delete
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            </CardHeader>

            {selectedSecret ? (
              <CardContent className="p-6 flex-1 min-h-0 overflow-y-auto">
                <Tabs defaultValue="formatted">
                  <TabsList className="grid w-full max-w-md grid-cols-2">
                    <TabsTrigger value="formatted">Formatted</TabsTrigger>
                    <TabsTrigger value="raw">Raw JSON</TabsTrigger>
                  </TabsList>

                  <TabsContent value="formatted" className="mt-6">
                    <div className="space-y-4">
                      {/* Add New Key Section */}
                      {isAddingNewKey ? (
                        <Card className="p-4 border-dashed">
                          <div className="space-y-3">
                            <div className="flex items-center justify-between">
                              <Label className="text-sm font-semibold">
                                Add New Key
                              </Label>
                              <div className="flex gap-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={saveNewKey}
                                >
                                  <Check className="h-4 w-4 text-green-600" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={cancelAddingNewKey}
                                >
                                  <X className="h-4 w-4 text-red-600" />
                                </Button>
                              </div>
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                              <Input
                                placeholder="Key name"
                                value={newKeyName}
                                onChange={(e) => setNewKeyName(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") {
                                    e.preventDefault();
                                    document
                                      .getElementById("new-value-input")
                                      ?.focus();
                                  } else if (e.key === "Escape") {
                                    cancelAddingNewKey();
                                  }
                                }}
                              />
                              <Input
                                id="new-value-input"
                                placeholder="Value"
                                value={newKeyValue}
                                onChange={(e) => setNewKeyValue(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter" && e.ctrlKey) {
                                    saveNewKey();
                                  } else if (e.key === "Escape") {
                                    cancelAddingNewKey();
                                  }
                                }}
                              />
                            </div>
                          </div>
                        </Card>
                      ) : (
                        <Card className="p-4 border-dashed">
                          <Button
                            variant="ghost"
                            onClick={startAddingNewKey}
                            className="w-full"
                          >
                            <Plus className="h-4 w-4 mr-2" />
                            Add New Key
                          </Button>
                        </Card>
                      )}
                      {Object.entries(selectedSecret.data).map(
                        ([key, value]) => (
                          <Card key={key} className="p-4">
                            <div className="flex items-center justify-between mb-2">
                              {editingKeyNames[key] ? (
                                <div className="flex items-center gap-2">
                                  <Input
                                    value={editKeyNames[key] || ""}
                                    onChange={(e) =>
                                      setEditKeyNames((prev) => ({
                                        ...prev,
                                        [key]: e.target.value,
                                      }))
                                    }
                                    className="text-sm font-semibold"
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter") {
                                        saveKeyNameEdit(key);
                                      } else if (e.key === "Escape") {
                                        cancelEditingKeyName(key);
                                      }
                                    }}
                                  />
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => saveKeyNameEdit(key)}
                                  >
                                    <Check className="h-4 w-4 text-green-600" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => cancelEditingKeyName(key)}
                                  >
                                    <X className="h-4 w-4 text-red-600" />
                                  </Button>
                                </div>
                              ) : (
                                <div className="flex items-center gap-2">
                                  <Label className="text-sm font-semibold">
                                    {key}
                                  </Label>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => startEditingKeyName(key)}
                                  >
                                    <Edit className="h-3 w-3" />
                                  </Button>
                                </div>
                              )}
                              <div className="flex gap-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => toggleValueVisibility(key)}
                                >
                                  {showValues[key] ? (
                                    <EyeOff className="h-4 w-4" />
                                  ) : (
                                    <Eye className="h-4 w-4" />
                                  )}
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => copyToClipboard(String(value))}
                                >
                                  <Copy className="h-4 w-4" />
                                </Button>
                                {editingKeys[key] ? (
                                  <>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => saveEdit(key)}
                                    >
                                      <Check className="h-4 w-4 text-green-600" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => cancelEditing(key)}
                                    >
                                      <X className="h-4 w-4 text-red-600" />
                                    </Button>
                                  </>
                                ) : (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => startEditing(key, value)}
                                  >
                                    <Edit className="h-4 w-4" />
                                  </Button>
                                )}
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => deleteKey(key)}
                                  className="text-red-600 hover:text-red-700"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>
                            <div className="bg-muted/30 p-3 rounded text-sm max-w-full overflow-hidden">
                              {editingKeys[key] ? (
                                <Textarea
                                  value={editValues[key] || ""}
                                  onChange={(e) =>
                                    setEditValues((prev) => ({
                                      ...prev,
                                      [key]: e.target.value,
                                    }))
                                  }
                                  className="font-mono resize-none min-h-[2.5rem] max-h-32 overflow-y-auto"
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter" && e.ctrlKey) {
                                      saveEdit(key);
                                    } else if (e.key === "Escape") {
                                      cancelEditing(key);
                                    }
                                  }}
                                />
                              ) : (
                                <div className="font-mono break-all whitespace-pre-wrap max-h-32 overflow-y-auto">
                                  {showValues[key]
                                    ? formatValueForDisplay(value)
                                    : "••••••••"}
                                </div>
                              )}
                            </div>
                          </Card>
                        ),
                      )}
                    </div>
                  </TabsContent>

                  <TabsContent value="raw" className="mt-6">
                    <div className="bg-muted/30 p-4 rounded relative max-w-full overflow-hidden">
                      {!isEditingRawJson && (
                        <div className="absolute top-2 right-2 flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={startEditingRawJson}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                      {isEditingRawJson ? (
                        <div className="space-y-3">
                          <div className="flex gap-2 justify-end">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={saveRawJsonEdit}
                            >
                              <Check className="h-4 w-4 text-green-600" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={cancelEditingRawJson}
                            >
                              <X className="h-4 w-4 text-red-600" />
                            </Button>
                          </div>
                          <Textarea
                            value={rawJsonValue}
                            onChange={(e) => setRawJsonValue(e.target.value)}
                            className="font-mono min-h-64 resize-none max-h-96 overflow-y-auto"
                            placeholder="Enter valid JSON..."
                            onKeyDown={(e) => {
                              if (e.key === "Enter" && e.ctrlKey) {
                                saveRawJsonEdit();
                              } else if (e.key === "Escape") {
                                cancelEditingRawJson();
                              }
                            }}
                          />
                        </div>
                      ) : (
                        <pre className="text-sm whitespace-pre-wrap font-mono break-all max-h-96 overflow-y-auto">
                          {JSON.stringify(selectedSecret.data, null, 2)}
                        </pre>
                      )}
                    </div>
                  </TabsContent>
                </Tabs>
              </CardContent>
            ) : (
              <CardContent className="flex flex-col items-center justify-center flex-1 min-h-0">
                <div className="p-4 rounded-full bg-muted/20 mb-6">
                  <Key className="h-12 w-12 text-muted-foreground" />
                </div>
                <h3 className="text-lg font-semibold mb-2">
                  No Secret Selected
                </h3>
                <p className="text-muted-foreground text-center max-w-md">
                  Something went wrong loading the secret.
                </p>
                <Button
                  variant="outline"
                  onClick={() => setViewMode("explorer")}
                  className="mt-4"
                >
                  Back to Explorer
                </Button>
              </CardContent>
            )}
          </Card>
        )}
      </div>

      {/* Delete Folder Confirmation Dialog */}
      <AlertDialog
        open={!!folderToDelete}
        onOpenChange={() => setFolderToDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Folder</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <p>
                  Are you sure you want to delete the folder "
                  {folderToDelete?.name}" and all its contents?
                </p>
                <div className="bg-muted/50 p-4 rounded-lg">
                  <p className="font-medium mb-2">This will delete:</p>
                  <ul className="space-y-1 text-sm">
                    <li>• {folderToDelete?.secretCount} secret(s)</li>
                    <li>• {folderToDelete?.folderCount} subfolder(s)</li>
                    <li className="font-medium pt-1 border-t border-border">
                      Total: {folderToDelete?.totalItems} item(s)
                    </li>
                  </ul>
                </div>
                <p className="text-destructive font-medium">
                  ⚠️ This action cannot be undone.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                deleteFolder();
                setFolderToDelete(null);
              }}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete Folder
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Key Confirmation Dialog */}
      <AlertDialog
        open={!!keyToDelete}
        onOpenChange={() => setKeyToDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Key</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the key "{keyToDelete}"? This
              action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteKey}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete Key
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
